import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Plus, RotateCcw, Trash2 } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  GRADING_SCALES,
  getScale,
  pointToLetter,
  type ScaleId,
} from "@/data/gradingScales";
import {
  calcSemesterGPA,
  calcCumulative,
  predictRequired,
  type SubjectRow,
  type SemesterRow,
} from "@/lib/calculators/gpa";

/* ------------ Strings ------------ */

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "GPA Calculator", bn: "জিপিএ ক্যালকুলেটর" },
  desc: {
    en: "Calculate GPA for SSC, HSC, DU, BUET, NU and private universities. Semester GPA, cumulative CGPA and target GPA predictor — built for Bangladesh.",
    bn: "এসএসসি, এইচএসসি, ঢাবি, বুয়েট, জাতীয় ও বেসরকারি বিশ্ববিদ্যালয়ের জিপিএ হিসাব করুন। সেমিস্টার জিপিএ, ক্রমপুঞ্জিত সিজিপিএ ও লক্ষ্য জিপিএ ভবিষ্যদ্বাণী — বাংলাদেশের জন্য তৈরি।",
  },

  scaleLabel: { en: "Grading System", bn: "গ্রেডিং সিস্টেম" },
  modeLabel: { en: "Mode", bn: "মোড" },
  modeSemester: { en: "Semester GPA", bn: "সেমিস্টার জিপিএ" },
  modeCumulative: { en: "Cumulative CGPA", bn: "সিজিপিএ" },
  modePredictor: { en: "Target Predictor", bn: "লক্ষ্য নির্ণয়" },

  subjects: { en: "Subjects / Courses", bn: "বিষয় / কোর্স" },
  subjectName: { en: "Subject", bn: "বিষয়" },
  grade: { en: "Grade", bn: "গ্রেড" },
  credit: { en: "Credit", bn: "ক্রেডিট" },
  addSubject: { en: "Add Subject", bn: "বিষয় যোগ করুন" },
  reset: { en: "Reset", bn: "রিসেট" },

  semesters: { en: "Semesters", bn: "সেমিস্টারসমূহ" },
  semName: { en: "Semester", bn: "সেমিস্টার" },
  semGpa: { en: "GPA", bn: "জিপিএ" },
  semCredits: { en: "Credits", bn: "ক্রেডিট" },
  addSemester: { en: "Add Semester", bn: "সেমিস্টার যোগ করুন" },

  yourGpa: { en: "Your GPA", bn: "আপনার জিপিএ" },
  yourCgpa: { en: "Your CGPA", bn: "আপনার সিজিপিএ" },
  letterGrade: { en: "Letter Grade", bn: "অক্ষর গ্রেড" },
  totalCredits: { en: "Total Credits", bn: "মোট ক্রেডিট" },
  totalPoints: { en: "Total Quality Points", bn: "মোট কোয়ালিটি পয়েন্ট" },
  contribution: { en: "Contribution", bn: "অবদান" },
  point: { en: "Point", bn: "পয়েন্ট" },

  // predictor
  currentCgpa: { en: "Current CGPA", bn: "বর্তমান সিজিপিএ" },
  currentCredits: { en: "Credits Earned", bn: "অর্জিত ক্রেডিট" },
  targetCgpa: { en: "Target CGPA", bn: "লক্ষ্য সিজিপিএ" },
  remainingCredits: { en: "Remaining Credits", bn: "বাকি ক্রেডিট" },
  required: {
    en: "You need a GPA of {n} in remaining {c} credits to reach CGPA {t}.",
    bn: "সিজিপিএ {t} পেতে বাকি {c} ক্রেডিটে আপনার {n} জিপিএ দরকার।",
  },
  notAchievable: {
    en: "Target CGPA {t} is not achievable with remaining {c} credits.",
    bn: "{c} ক্রেডিটে {t} সিজিপিএ অর্জন সম্ভব নয়।",
  },
  alreadyAbove: {
    en: "You can reach the target by maintaining a GPA of about {n}.",
    bn: "প্রায় {n} জিপিএ ধরে রাখলেই লক্ষ্যে পৌঁছাবেন।",
  },

  // errors
  errNoSubjects: { en: "Add at least one subject.", bn: "অন্তত একটি বিষয় যোগ করুন।" },
  errZeroCredits: { en: "Total credits cannot be zero.", bn: "মোট ক্রেডিট শূন্য হতে পারে না।" },
  errInvalidCredit: { en: "Credit must be between 1 and 6.", bn: "ক্রেডিট ১ থেকে ৬ এর মধ্যে হতে হবে।" },

  source: { en: "Source", bn: "উৎস" },

  // content
  howTitle: { en: "How GPA is calculated in Bangladesh", bn: "বাংলাদেশে জিপিএ কীভাবে গণনা করা হয়" },
  howBody: {
    en: "GPA = Σ(Grade Point × Credit) / Σ(Credit). Each course's grade is converted to a numeric grade point, multiplied by its credit weight, summed, then divided by the total credits.",
    bn: "জিপিএ = Σ(গ্রেড পয়েন্ট × ক্রেডিট) / Σ(ক্রেডিট)। প্রতিটি কোর্সের গ্রেডকে সংখ্যাসূচক পয়েন্টে রূপান্তর করে ক্রেডিট দিয়ে গুণ করে যোগ করা হয়, এরপর মোট ক্রেডিট দিয়ে ভাগ করা হয়।",
  },
  sscTitle: { en: "SSC GPA calculation (5.00 scale)", bn: "এসএসসি জিপিএ গণনা (৫.০০)" },
  sscBody: {
    en: "Bangladesh Education Board uses a 5.00 scale: A+ = 5.00, A = 4.00, A- = 3.50, B = 3.00, C = 2.00, D = 1.00, F = 0.00. The fourth subject's grade above 2.00 is added to the GPA but capped at 5.00.",
    bn: "বাংলাদেশ শিক্ষা বোর্ড ৫.০০ স্কেল ব্যবহার করে: A+=৫.০০, A=৪.০০, A-=৩.৫০, B=৩.০০, C=২.০০, D=১.০০, F=০.০০। চতুর্থ বিষয়ের ২.০০-এর বেশি গ্রেড পয়েন্ট জিপিএতে যোগ হয়, তবে সর্বোচ্চ ৫.০০।",
  },
  hscTitle: { en: "HSC GPA calculation", bn: "এইচএসসি জিপিএ গণনা" },
  hscBody: {
    en: "HSC follows the same 5.00 scale as SSC. Group subjects (Science, Business Studies, Humanities) and the fourth-subject rule apply identically.",
    bn: "এইচএসসিতেও একই ৫.০০ স্কেল প্রযোজ্য। বিজ্ঞান/ব্যবসা/মানবিক গ্রুপের বিষয় ও চতুর্থ বিষয়ের নিয়ম একই থাকে।",
  },
  uniTitle: { en: "University CGPA in Bangladesh", bn: "বাংলাদেশে বিশ্ববিদ্যালয় সিজিপিএ" },
  uniBody: {
    en: "Most public universities (DU, BUET, NU, RU, CU) and private universities (NSU, BRACU, IUB, EWU) use a 4.00 scale with letter grades from A+ to F. Cut-offs vary slightly between institutions but the calculation method is identical.",
    bn: "অধিকাংশ পাবলিক বিশ্ববিদ্যালয় (ঢাবি, বুয়েট, জাবি, রাবি, চবি) এবং বেসরকারি বিশ্ববিদ্যালয় (এনএসইউ, ব্র্যাক, আইইউবি, ইডব্লিউইউ) ৪.০০ স্কেলে A+ থেকে F পর্যন্ত গ্রেড দেয়। প্রতিষ্ঠানভেদে কাটঅফ সামান্য পরিবর্তিত হলেও গণনা পদ্ধতি একই।",
  },
  pctTitle: { en: "GPA to percentage conversion", bn: "জিপিএ থেকে শতকরা" },
  pctBody: {
    en: "Approximate ranges only — institutions may have their own conversions. SSC/HSC: A+ ≥ 80%, A 70–79%, A- 60–69%. University 4.00: A+ ≥ 80%, A 75–79%, B 60–64%.",
    bn: "এই রূপান্তর আনুমানিক — প্রতিষ্ঠানভেদে ভিন্ন হতে পারে। এসএসসি/এইচএসসি: A+ ≥ ৮০%, A ৭০–৭৯%, A- ৬০–৬৯%। বিশ্ববিদ্যালয় ৪.০০: A+ ≥ ৮০%, A ৭৫–৭৯%, B ৬০–৬৪%।",
  },
  cgpaTitle: { en: "GPA to CGPA conversion", bn: "জিপিএ থেকে সিজিপিএ" },
  cgpaBody: {
    en: "CGPA = Σ(Semester GPA × Semester Credits) / Σ(Total Credits). Each semester is weighted by the credits taken that term, not by a flat average.",
    bn: "সিজিপিএ = Σ(সেমিস্টার জিপিএ × সেমিস্টার ক্রেডিট) / Σ(মোট ক্রেডিট)। প্রতিটি সেমিস্টার সেই সেমিস্টারের ক্রেডিট দিয়ে ওজনযুক্ত হয়, সরল গড় নয়।",
  },
  tipsTitle: { en: "Tips to improve your GPA", bn: "জিপিএ বাড়ানোর উপায়" },
  tipsBody: {
    en: "Focus on high-credit courses first — they move your GPA most. Attend every class, take notes by hand, study in 45–60 minute blocks with breaks, form a small study group, and meet your instructors during office hours when stuck.",
    bn: "প্রথমে বেশি ক্রেডিটের কোর্সে মনোযোগ দিন — সেগুলোই জিপিএ সবচেয়ে বেশি বদলায়। প্রতিটি ক্লাসে উপস্থিত থাকুন, হাতে নোট নিন, ৪৫–৬০ মিনিট পর বিরতি দিয়ে পড়ুন, ছোট স্টাডি গ্রুপ করুন, এবং আটকে গেলে শিক্ষকের অফিস আওয়ারে দেখা করুন।",
  },
} as const;

/* ------------ FAQ ------------ */

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How is SSC GPA calculated?", bn: "SSC এ জিপিএ কীভাবে গণনা হয়?" },
    a: {
      en: "Each subject's grade point (A+=5, A=4, A-=3.5, …) is multiplied by its credit, summed, and divided by total credits. The fourth subject's grade above 2.00 is added separately and the result is capped at 5.00.",
      bn: "প্রতিটি বিষয়ের গ্রেড পয়েন্ট (A+=৫, A=৪, A-=৩.৫, …) ক্রেডিট দিয়ে গুণ করে যোগ করে মোট ক্রেডিট দিয়ে ভাগ করা হয়। চতুর্থ বিষয়ের ২.০০-এর অতিরিক্ত পয়েন্ট আলাদা যোগ হয় এবং সর্বোচ্চ ৫.০০।",
    },
  },
  {
    q: { en: "What is the highest GPA in HSC?", bn: "এইচএসসিতে সর্বোচ্চ জিপিএ কত?" },
    a: {
      en: "The highest possible HSC GPA is 5.00 — awarded when a student secures A+ in every subject including the fourth.",
      bn: "এইচএসসিতে সর্বোচ্চ জিপিএ ৫.০০ — সব বিষয়ে (চতুর্থ সহ) A+ পেলে পাওয়া যায়।",
    },
  },
  {
    q: { en: "How is CGPA different from GPA?", bn: "জিপিএ ও সিজিপিএ-র পার্থক্য কী?" },
    a: {
      en: "GPA is the grade point average for one semester. CGPA is the cumulative average across all completed semesters, weighted by credits.",
      bn: "জিপিএ একটি সেমিস্টারের গড়। সিজিপিএ সব সেমিস্টারের ক্রমপুঞ্জিত গড়, ক্রেডিট ভিত্তিক ওজনে।",
    },
  },
  {
    q: { en: "What is a good GPA in Bangladesh?", bn: "বাংলাদেশে ভালো জিপিএ কত?" },
    a: {
      en: "On the 5.00 scale, 4.50 and above is considered very good. On the university 4.00 scale, 3.50+ is strong and typically required for scholarships and graduate admissions.",
      bn: "৫.০০ স্কেলে ৪.৫০+ খুব ভালো ধরা হয়। বিশ্ববিদ্যালয় ৪.০০ স্কেলে ৩.৫০+ শক্তিশালী এবং বৃত্তি বা স্নাতকোত্তর ভর্তির জন্য সাধারণত প্রয়োজন।",
    },
  },
  {
    q: { en: "How do I calculate my cumulative CGPA?", bn: "ক্রমপুঞ্জিত সিজিপিএ কীভাবে বের করব?" },
    a: {
      en: "Multiply each semester's GPA by its credits, sum across all semesters, then divide by the total credits. Use the Cumulative CGPA mode above to do this automatically.",
      bn: "প্রতিটি সেমিস্টারের জিপিএ-কে সেই সেমিস্টারের ক্রেডিট দিয়ে গুণ করে যোগ করুন, এরপর মোট ক্রেডিট দিয়ে ভাগ করুন। উপরে \"সিজিপিএ\" মোড ব্যবহার করুন।",
    },
  },
  {
    q: { en: "SSC এ জিপিএ কীভাবে গণনা হয়?", bn: "SSC এ জিপিএ কীভাবে গণনা হয়?" },
    a: {
      en: "প্রতিটি বিষয়ের গ্রেড পয়েন্টকে ক্রেডিট দিয়ে গুণ করে যোগফলকে মোট ক্রেডিট দিয়ে ভাগ করুন। চতুর্থ বিষয়ের ২.০০-এর অতিরিক্ত পয়েন্ট যোগ হয়, সর্বোচ্চ ৫.০০।",
      bn: "প্রতিটি বিষয়ের গ্রেড পয়েন্টকে ক্রেডিট দিয়ে গুণ করে যোগফলকে মোট ক্রেডিট দিয়ে ভাগ করুন। চতুর্থ বিষয়ের ২.০০-এর অতিরিক্ত পয়েন্ট যোগ হয়, সর্বোচ্চ ৫.০০।",
    },
  },
  {
    q: { en: "এইচএসসিতে জিপিএ ৫ পেতে কত নম্বর লাগে?", bn: "এইচএসসিতে জিপিএ ৫ পেতে কত নম্বর লাগে?" },
    a: {
      en: "প্রতিটি বিষয়ে ন্যূনতম ৮০% নম্বর প্রয়োজন (চতুর্থ বিষয় সহ) — A+ পাওয়ার শর্ত।",
      bn: "প্রতিটি বিষয়ে ন্যূনতম ৮০% নম্বর প্রয়োজন (চতুর্থ বিষয় সহ) — A+ পাওয়ার শর্ত।",
    },
  },
  {
    q: { en: "ঢাকা বিশ্ববিদ্যালয়ের সিজিপিএ কীভাবে গণনা করা হয়?", bn: "ঢাকা বিশ্ববিদ্যালয়ের সিজিপিএ কীভাবে গণনা করা হয়?" },
    a: {
      en: "ঢাবি ৪.০০ স্কেলে A+ = ৪.০০, A = ৩.৭৫ … । প্রতিটি কোর্সের পয়েন্ট × ক্রেডিট যোগ করে মোট ক্রেডিট দিয়ে ভাগ করলে সিজিপিএ পাওয়া যায়।",
      bn: "ঢাবি ৪.০০ স্কেলে A+ = ৪.০০, A = ৩.৭৫ … । প্রতিটি কোর্সের পয়েন্ট × ক্রেডিট যোগ করে মোট ক্রেডিট দিয়ে ভাগ করলে সিজিপিএ পাওয়া যায়।",
    },
  },
  {
    q: { en: "জিপিএ থেকে পার্সেন্টেজ কত হয়?", bn: "জিপিএ থেকে পার্সেন্টেজ কত হয়?" },
    a: {
      en: "আনুমানিক: ৫.০০ ≈ ৮০–১০০%, ৪.০০ ≈ ৭০–৭৯%, ৩.৫০ ≈ ৬০–৬৯%, ৩.০০ ≈ ৫০–৫৯%। সঠিক রূপান্তর প্রতিষ্ঠানভেদে ভিন্ন।",
      bn: "আনুমানিক: ৫.০০ ≈ ৮০–১০০%, ৪.০০ ≈ ৭০–৭৯%, ৩.৫০ ≈ ৬০–৬৯%, ৩.০০ ≈ ৫০–৫৯%। সঠিক রূপান্তর প্রতিষ্ঠানভেদে ভিন্ন।",
    },
  },
  {
    q: { en: "GPA ৪.০০ scale এ A+ কত?", bn: "GPA ৪.০০ scale এ A+ কত?" },
    a: {
      en: "৪.০০ স্কেলে A+ = ৪.০০ (পাবলিক বিশ্ববিদ্যালয়), যা ৮০% বা তার বেশি নম্বরের জন্য দেওয়া হয়।",
      bn: "৪.০০ স্কেলে A+ = ৪.০০ (পাবলিক বিশ্ববিদ্যালয়), যা ৮০% বা তার বেশি নম্বরের জন্য দেওয়া হয়।",
    },
  },
];

/* ------------ Route ------------ */

export const Route = createFileRoute("/gpa-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "GPA Calculator BD — SSC, HSC, University CGPA",
      description:
        "Free GPA calculator for Bangladesh — SSC, HSC, DU, BUET, NU, private universities. Calculate semester GPA, cumulative CGPA, predict required GPA. জিপিএ ক্যালকুলেটর।",
      path: "/gpa-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "GPA Calculator", path: "/gpa-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: GPACalculatorPage,
});

/* ------------ Page ------------ */

type Mode = "semester" | "cumulative" | "predictor";

const STORAGE_KEY = "gpa-calculator:v1";

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

function defaultRows(scaleId: ScaleId): SubjectRow[] {
  const top = getScale(scaleId).grades[0].letter;
  return [
    { id: makeId(), name: "", letter: top, credit: scaleId === "ssc_hsc" ? 4 : 3 },
    { id: makeId(), name: "", letter: top, credit: scaleId === "ssc_hsc" ? 4 : 3 },
    { id: makeId(), name: "", letter: top, credit: scaleId === "ssc_hsc" ? 4 : 3 },
  ];
}

function GPACalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt2 = (n: number) => num(n.toFixed(2));

  const [scaleId, setScaleId] = useState<ScaleId>("ssc_hsc");
  const [mode, setMode] = useState<Mode>("semester");
  const [rows, setRows] = useState<SubjectRow[]>(() => defaultRows("ssc_hsc"));
  const [semesters, setSemesters] = useState<SemesterRow[]>(() => [
    { id: makeId(), name: "Semester 1", gpa: 3.5, credits: 15 },
    { id: makeId(), name: "Semester 2", gpa: 3.7, credits: 15 },
  ]);

  // Predictor inputs
  const [pCurrentCgpa, setPCurrentCgpa] = useState("3.50");
  const [pCurrentCredits, setPCurrentCredits] = useState("60");
  const [pTargetCgpa, setPTargetCgpa] = useState("3.70");
  const [pRemainingCredits, setPRemainingCredits] = useState("40");

  const scale = getScale(scaleId);

  // Restore from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.scaleId) setScaleId(parsed.scaleId);
      if (parsed.mode) setMode(parsed.mode);
      if (Array.isArray(parsed.rows) && parsed.rows.length) setRows(parsed.rows);
      if (Array.isArray(parsed.semesters) && parsed.semesters.length) setSemesters(parsed.semesters);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ scaleId, mode, rows, semesters }),
      );
    } catch {
      /* ignore */
    }
  }, [scaleId, mode, rows, semesters]);

  // When scale changes, normalise letters to nearest valid grade for new scale
  useEffect(() => {
    setRows((prev) =>
      prev.map((r) => {
        const exists = scale.grades.find((g) => g.letter === r.letter);
        return exists ? r : { ...r, letter: scale.grades[0].letter };
      }),
    );
  }, [scaleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const semResult = useMemo(() => calcSemesterGPA(rows, scale), [rows, scale]);
  const cumResult = useMemo(() => calcCumulative(semesters), [semesters]);
  const predResult = useMemo(
    () =>
      predictRequired({
        currentCgpa: parseFloat(pCurrentCgpa) || 0,
        currentCredits: parseFloat(pCurrentCredits) || 0,
        targetCgpa: parseFloat(pTargetCgpa) || 0,
        remainingCredits: parseFloat(pRemainingCredits) || 0,
        maxScale: scale.max,
      }),
    [pCurrentCgpa, pCurrentCredits, pTargetCgpa, pRemainingCredits, scale.max],
  );

  const addRow = () =>
    setRows((p) => [
      ...p,
      { id: makeId(), name: "", letter: scale.grades[0].letter, credit: scaleId === "ssc_hsc" ? 4 : 3 },
    ]);
  const removeRow = (id: string) => setRows((p) => p.filter((r) => r.id !== id));
  const updateRow = (id: string, patch: Partial<SubjectRow>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addSemester = () =>
    setSemesters((p) => [
      ...p,
      { id: makeId(), name: `Semester ${p.length + 1}`, gpa: 3.5, credits: 15 },
    ]);
  const removeSemester = (id: string) => setSemesters((p) => p.filter((r) => r.id !== id));
  const updateSemester = (id: string, patch: Partial<SemesterRow>) =>
    setSemesters((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const reset = () => {
    setScaleId("ssc_hsc");
    setMode("semester");
    setRows(defaultRows("ssc_hsc"));
    setSemesters([
      { id: makeId(), name: "Semester 1", gpa: 3.5, credits: 15 },
      { id: makeId(), name: "Semester 2", gpa: 3.7, credits: 15 },
    ]);
    setPCurrentCgpa("3.50");
    setPCurrentCredits("60");
    setPTargetCgpa("3.70");
    setPRemainingCredits("40");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  const errMsg = (() => {
    if (mode === "semester" && !semResult.ok) {
      if (semResult.error === "no_subjects") return tr("errNoSubjects");
      if (semResult.error === "zero_credits") return tr("errZeroCredits");
      if (semResult.error === "invalid_credit") return tr("errInvalidCredit");
    }
    if (mode === "cumulative" && !cumResult.ok) {
      if (cumResult.error === "no_subjects") return tr("errNoSubjects");
      if (cumResult.error === "zero_credits") return tr("errZeroCredits");
    }
    return null;
  })();

  const shareLine = (() => {
    if (mode === "semester" && semResult.ok)
      return `${tr("yourGpa")}: ${fmt2(semResult.gpa)} / ${fmt2(scale.max)}`;
    if (mode === "cumulative" && cumResult.ok)
      return `${tr("yourCgpa")}: ${fmt2(cumResult.cgpa)} / ${fmt2(scale.max)}`;
    if (mode === "predictor" && predResult.ok)
      return `${tr("required").replace("{n}", fmt2(predResult.required)).replace("{c}", num(pRemainingCredits)).replace("{t}", num(pTargetCgpa))}`;
    return "";
  })();

  return (
    <CalculatorLayout
      slug="gpa-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="GraduationCap"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Top toggles */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
            {tr("scaleLabel")}
          </label>
          <select
            value={scaleId}
            onChange={(e) => setScaleId(e.target.value as ScaleId)}
            className={cn(
              "w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-sm outline-none transition focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20 md:w-80",
              lang === "bn" && "font-bn",
            )}
          >
            {GRADING_SCALES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name[lang]}
              </option>
            ))}
          </select>
          <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("source")}: {scale.source[lang]}
          </p>
        </div>

        <ModeToggle mode={mode} onChange={setMode} lang={lang} />
      </div>

      {/* Mode-specific UI */}
      {mode === "semester" && (
        <SemesterEditor
          rows={rows}
          scale={scale}
          lang={lang}
          onAdd={addRow}
          onRemove={removeRow}
          onUpdate={updateRow}
          tr={tr}
        />
      )}

      {mode === "cumulative" && (
        <CumulativeEditor
          rows={semesters}
          maxScale={scale.max}
          lang={lang}
          onAdd={addSemester}
          onRemove={removeSemester}
          onUpdate={updateSemester}
          tr={tr}
        />
      )}

      {mode === "predictor" && (
        <PredictorEditor
          values={{
            currentCgpa: pCurrentCgpa,
            currentCredits: pCurrentCredits,
            targetCgpa: pTargetCgpa,
            remainingCredits: pRemainingCredits,
          }}
          onChange={{
            currentCgpa: setPCurrentCgpa,
            currentCredits: setPCurrentCredits,
            targetCgpa: setPTargetCgpa,
            remainingCredits: setPRemainingCredits,
          }}
          lang={lang}
          tr={tr}
        />
      )}

      {/* Error message */}
      {errMsg && (
        <p className={cn("mt-4 text-sm text-destructive", lang === "bn" && "font-bn")}>
          {errMsg}
        </p>
      )}

      {/* Result panel */}
      {!errMsg && (
        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-2xl border border-border bg-gradient-to-br from-accent/10 via-card to-card p-5 md:p-6"
        >
          {mode === "semester" && semResult.ok && (
            <SemesterResultBlock
              gpa={semResult.gpa}
              totalCredits={semResult.totalCredits}
              totalPoints={semResult.totalPoints}
              perRow={semResult.perRow}
              scaleMax={scale.max}
              scaleId={scaleId}
              lang={lang}
              tr={tr}
              num={num}
              fmt2={fmt2}
            />
          )}

          {mode === "cumulative" && cumResult.ok && (
            <CumulativeResultBlock
              cgpa={cumResult.cgpa}
              totalCredits={cumResult.totalCredits}
              semesters={semesters}
              scaleMax={scale.max}
              scaleId={scaleId}
              lang={lang}
              tr={tr}
              num={num}
              fmt2={fmt2}
            />
          )}

          {mode === "predictor" && predResult.ok && (
            <PredictorResultBlock
              result={predResult}
              targetCgpa={pTargetCgpa}
              remainingCredits={pRemainingCredits}
              scaleMax={scale.max}
              lang={lang}
              tr={tr}
              num={num}
              fmt2={fmt2}
            />
          )}

          {shareLine && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <CopyResultButton text={shareLine} />
              <ShareButton title={shareLine} />
              <button
                type="button"
                onClick={reset}
                className={cn(
                  "ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:border-accent hover:text-accent",
                  lang === "bn" && "font-bn",
                )}
              >
                <RotateCcw size={14} /> {tr("reset")}
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Grade reference table for current scale */}
      <section className="mt-8">
        <h3 className={cn("mb-3 text-sm font-semibold", lang === "bn" && "font-bn")}>
          {scale.name[lang]}
        </h3>
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>
                  {tr("letterGrade")}
                </th>
                <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>
                  {tr("point")}
                </th>
                <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>%</th>
              </tr>
            </thead>
            <tbody>
              {scale.grades.map((g) => (
                <tr key={g.letter} className="border-t border-border">
                  <td className="px-3 py-2 font-semibold">{g.letter}</td>
                  <td className="px-3 py-2 tabular-nums">{num(g.point.toFixed(2))}</td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">
                    {g.pct ? `${num(g.pct[0])}–${num(g.pct[1])}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={tr("howTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("howBody")}
          </p>
        </Section>
        <Section title={tr("sscTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("sscBody")}
          </p>
        </Section>
        <Section title={tr("hscTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("hscBody")}
          </p>
        </Section>
        <Section title={tr("uniTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("uniBody")}
          </p>
        </Section>
        <Section title={tr("pctTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("pctBody")}
          </p>
        </Section>
        <Section title={tr("cgpaTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("cgpaBody")}
          </p>
        </Section>
        <Section title={tr("tipsTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("tipsBody")}
          </p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ------------ Sub-components ------------ */

function ModeToggle({
  mode,
  onChange,
  lang,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
  lang: "en" | "bn";
}) {
  const opts: { id: Mode; label: Bilingual }[] = [
    { id: "semester", label: STR.modeSemester },
    { id: "cumulative", label: STR.modeCumulative },
    { id: "predictor", label: STR.modePredictor },
  ];
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
        {STR.modeLabel[lang]}
      </label>
      <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-0.5">
        {opts.map((o) => {
          const active = o.id === mode;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition",
                active
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn",
              )}
            >
              {o.label[lang]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SemesterEditor({
  rows,
  scale,
  lang,
  onAdd,
  onRemove,
  onUpdate,
  tr,
}: {
  rows: SubjectRow[];
  scale: ReturnType<typeof getScale>;
  lang: "en" | "bn";
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, patch: Partial<SubjectRow>) => void;
  tr: (k: keyof typeof STR) => string;
}) {
  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
          {tr("subjects")}
        </h3>
        <button
          type="button"
          onClick={onAdd}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium hover:border-accent hover:text-accent",
            lang === "bn" && "font-bn",
          )}
        >
          <Plus size={14} /> {tr("addSubject")}
        </button>
      </div>

      <div className="space-y-2">
        {rows.map((r, idx) => (
          <div
            key={r.id}
            className="grid grid-cols-12 items-end gap-2 rounded-xl border border-border bg-card p-3"
          >
            <div className="col-span-12 md:col-span-5">
              <label className={cn("mb-1 block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("subjectName")} {idx + 1}
              </label>
              <input
                value={r.name}
                onChange={(e) => onUpdate(r.id, { name: e.target.value })}
                placeholder={lang === "bn" ? `বিষয় ${idx + 1}` : `Subject ${idx + 1}`}
                className={cn(
                  "w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20",
                  lang === "bn" && "font-bn",
                )}
              />
            </div>
            <div className="col-span-6 md:col-span-4">
              <label className={cn("mb-1 block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("grade")}
              </label>
              <select
                value={r.letter}
                onChange={(e) => onUpdate(r.id, { letter: e.target.value })}
                className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
              >
                {scale.grades.map((g) => (
                  <option key={g.letter} value={g.letter}>
                    {g.letter} ({g.point.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-5 md:col-span-2">
              <NumberInput
                label={tr("credit")}
                value={String(r.credit)}
                onChange={(v) => onUpdate(r.id, { credit: Math.max(0, Math.min(6, parseFloat(v) || 0)) })}
                min={1}
                max={6}
                step={0.5}
              />
            </div>
            <div className="col-span-1 flex justify-end">
              <button
                type="button"
                onClick={() => onRemove(r.id)}
                disabled={rows.length === 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition hover:border-destructive hover:text-destructive disabled:opacity-40"
                aria-label="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CumulativeEditor({
  rows,
  maxScale,
  lang,
  onAdd,
  onRemove,
  onUpdate,
  tr,
}: {
  rows: SemesterRow[];
  maxScale: number;
  lang: "en" | "bn";
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, patch: Partial<SemesterRow>) => void;
  tr: (k: keyof typeof STR) => string;
}) {
  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
          {tr("semesters")}
        </h3>
        <button
          type="button"
          onClick={onAdd}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium hover:border-accent hover:text-accent",
            lang === "bn" && "font-bn",
          )}
        >
          <Plus size={14} /> {tr("addSemester")}
        </button>
      </div>
      <div className="space-y-2">
        {rows.map((r, idx) => (
          <div
            key={r.id}
            className="grid grid-cols-12 items-end gap-2 rounded-xl border border-border bg-card p-3"
          >
            <div className="col-span-12 md:col-span-5">
              <label className={cn("mb-1 block text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("semName")} {idx + 1}
              </label>
              <input
                value={r.name}
                onChange={(e) => onUpdate(r.id, { name: e.target.value })}
                className={cn(
                  "w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20",
                  lang === "bn" && "font-bn",
                )}
              />
            </div>
            <div className="col-span-6 md:col-span-3">
              <NumberInput
                label={tr("semGpa")}
                value={String(r.gpa)}
                onChange={(v) => onUpdate(r.id, { gpa: Math.max(0, Math.min(maxScale, parseFloat(v) || 0)) })}
                min={0}
                max={maxScale}
                step={0.01}
              />
            </div>
            <div className="col-span-5 md:col-span-3">
              <NumberInput
                label={tr("semCredits")}
                value={String(r.credits)}
                onChange={(v) => onUpdate(r.id, { credits: Math.max(0, parseFloat(v) || 0) })}
                min={0}
                step={1}
              />
            </div>
            <div className="col-span-1 flex justify-end">
              <button
                type="button"
                onClick={() => onRemove(r.id)}
                disabled={rows.length === 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition hover:border-destructive hover:text-destructive disabled:opacity-40"
                aria-label="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PredictorEditor({
  values,
  onChange,
  lang,
  tr,
}: {
  values: { currentCgpa: string; currentCredits: string; targetCgpa: string; remainingCredits: string };
  onChange: {
    currentCgpa: (v: string) => void;
    currentCredits: (v: string) => void;
    targetCgpa: (v: string) => void;
    remainingCredits: (v: string) => void;
  };
  lang: "en" | "bn";
  tr: (k: keyof typeof STR) => string;
}) {
  return (
    <div className="mt-6 grid gap-4 md:grid-cols-2">
      <NumberInput
        label={tr("currentCgpa")}
        value={values.currentCgpa}
        onChange={onChange.currentCgpa}
        step={0.01}
      />
      <NumberInput
        label={tr("currentCredits")}
        value={values.currentCredits}
        onChange={onChange.currentCredits}
        step={1}
      />
      <NumberInput
        label={tr("targetCgpa")}
        value={values.targetCgpa}
        onChange={onChange.targetCgpa}
        step={0.01}
      />
      <NumberInput
        label={tr("remainingCredits")}
        value={values.remainingCredits}
        onChange={onChange.remainingCredits}
        step={1}
      />
    </div>
  );
}

function SemesterResultBlock({
  gpa,
  totalCredits,
  totalPoints,
  perRow,
  scaleMax,
  scaleId,
  lang,
  tr,
  num,
  fmt2,
}: {
  gpa: number;
  totalCredits: number;
  totalPoints: number;
  perRow: { row: SubjectRow; point: number; contribution: number }[];
  scaleMax: number;
  scaleId: ScaleId;
  lang: "en" | "bn";
  tr: (k: keyof typeof STR) => string;
  num: (n: number | string) => string;
  fmt2: (n: number) => string;
}) {
  const scale = getScale(scaleId);
  const letter = pointToLetter(scale, gpa);
  const pct = Math.min(100, (gpa / scaleMax) * 100);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("yourGpa")}
          </p>
          <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
            <span>{fmt2(gpa)}</span>
            <span className="ml-1 text-2xl font-medium text-muted-foreground">/ {fmt2(scaleMax)}</span>
          </p>
        </div>
        <div className="text-right">
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("letterGrade")}
          </p>
          <p className="mt-1 text-3xl font-bold text-accent">{letter}</p>
        </div>
      </div>

      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
        <motion.div
          className="h-full rounded-full bg-accent"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Stat label={tr("totalCredits")} value={num(totalCredits)} lang={lang} />
        <Stat label={tr("totalPoints")} value={num(totalPoints.toFixed(2))} lang={lang} />
        <Stat label={tr("letterGrade")} value={letter} lang={lang} />
      </div>

      {/* Per-subject contribution */}
      <div className="mt-5 overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>
                {tr("subjectName")}
              </th>
              <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("grade")}</th>
              <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("credit")}</th>
              <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>
                {tr("contribution")}
              </th>
            </tr>
          </thead>
          <tbody>
            {perRow.map((r, i) => (
              <tr key={r.row.id} className="border-t border-border">
                <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>
                  {r.row.name || (lang === "bn" ? `বিষয় ${num(i + 1)}` : `Subject ${i + 1}`)}
                </td>
                <td className="px-3 py-2 font-medium">
                  {r.row.letter} <span className="text-muted-foreground">({num(r.point.toFixed(2))})</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{num(r.row.credit)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(r.contribution.toFixed(2))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CumulativeResultBlock({
  cgpa,
  totalCredits,
  semesters,
  scaleMax,
  scaleId,
  lang,
  tr,
  num,
  fmt2,
}: {
  cgpa: number;
  totalCredits: number;
  semesters: SemesterRow[];
  scaleMax: number;
  scaleId: ScaleId;
  lang: "en" | "bn";
  tr: (k: keyof typeof STR) => string;
  num: (n: number | string) => string;
  fmt2: (n: number) => string;
}) {
  const scale = getScale(scaleId);
  const letter = pointToLetter(scale, cgpa);
  const pct = Math.min(100, (cgpa / scaleMax) * 100);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("yourCgpa")}
          </p>
          <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
            <span>{fmt2(cgpa)}</span>
            <span className="ml-1 text-2xl font-medium text-muted-foreground">/ {fmt2(scaleMax)}</span>
          </p>
        </div>
        <div className="text-right">
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("letterGrade")}
          </p>
          <p className="mt-1 text-3xl font-bold text-accent">{letter}</p>
        </div>
      </div>

      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
        <motion.div
          className="h-full rounded-full bg-accent"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <Stat label={tr("totalCredits")} value={num(totalCredits)} lang={lang} />
        <Stat label={tr("letterGrade")} value={letter} lang={lang} />
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("semName")}</th>
              <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("semGpa")}</th>
              <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("semCredits")}</th>
              <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("contribution")}</th>
            </tr>
          </thead>
          <tbody>
            {semesters.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{s.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(s.gpa.toFixed(2))}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(s.credits)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num((s.gpa * s.credits).toFixed(2))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PredictorResultBlock({
  result,
  targetCgpa,
  remainingCredits,
  scaleMax,
  lang,
  tr,
  num,
  fmt2,
}: {
  result: { required: number; achievable: boolean };
  targetCgpa: string;
  remainingCredits: string;
  scaleMax: number;
  lang: "en" | "bn";
  tr: (k: keyof typeof STR) => string;
  num: (n: number | string) => string;
  fmt2: (n: number) => string;
}) {
  if (!result.achievable && result.required > scaleMax) {
    return (
      <div>
        <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("required").split("{")[0].trim()}
        </p>
        <p className={cn("mt-2 text-lg font-semibold text-destructive", lang === "bn" && "font-bn")}>
          {tr("notAchievable").replace("{c}", num(remainingCredits)).replace("{t}", num(targetCgpa))}
        </p>
      </div>
    );
  }

  if (result.required <= 0) {
    return (
      <div>
        <p className={cn("text-base", lang === "bn" && "font-bn")}>
          {tr("alreadyAbove").replace("{n}", fmt2(Math.max(0, result.required)))}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
        {tr("required").split("{")[0].trim()}
      </p>
      <p className="mt-2 text-3xl font-bold tabular-nums text-accent md:text-4xl">
        {fmt2(result.required)} <span className="text-base font-medium text-muted-foreground">/ {fmt2(scaleMax)}</span>
      </p>
      <p className={cn("mt-3 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
        {tr("required")
          .replace("{n}", fmt2(result.required))
          .replace("{c}", num(remainingCredits))
          .replace("{t}", num(targetCgpa))}
      </p>
    </div>
  );
}

function Stat({ label, value, lang }: { label: string; value: string; lang: "en" | "bn" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{label}</p>
      <p className={cn("mt-1 text-lg font-semibold tabular-nums", lang === "bn" && "font-bn")}>{value}</p>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
